/**
 * Lead schema — the typed shape the Intake Verification surface (and
 * everything else that reads Leads) consumes.
 *
 * The schema reflects the parser's effective contract:
 *   - Always present (parser writes unconditionally):
 *       `leadId`, `name`, `caseType`, `status`, `caseSummary`,
 *       `createdTime` (Airtable system), `id` (Airtable system),
 *       `signed` (default false), `intakeDocs` (default []).
 *   - Optional (parser writes only when extractable from the source
 *     email):
 *       `phone`, `email`, `incidentDate`, `incidentState`,
 *       `originatingFirmId`, `internalNotes`, `senderEmail`,
 *       `senderDomain`.
 *
 * Optional fields are modeled as `T | null`. The projection layer
 * converts absent Airtable fields to `null` so consumers don't have
 * to discriminate `undefined` from `null`.
 *
 * `originatingFirmId` is the linked-record ID (a string), not the
 * Firm Name. Joining IDs to names is the composition layer's job —
 * keeps this schema single-table.
 *
 * Status enum is deliberately permissive (string). The Airtable
 * `Status` enum has 11 members and the parser does not constrain to
 * a Zod literal union here; downstream code that branches on status
 * does its own narrowing.
 */

import { z } from "zod";

export const LeadAttachmentSchema = z.object({
  /** Airtable attachment ID. */
  id: z.string(),
  filename: z.string(),
  /** Bytes. */
  size: z.number(),
  url: z.string().url(),
});
export type LeadAttachment = z.infer<typeof LeadAttachmentSchema>;

export const LeadSchema = z.object({
  /** Airtable record ID. */
  id: z.string(),
  /** ISO-8601 datetime — Airtable's system createdTime. */
  createdTime: z.string(),

  /** Stable human-readable Lead ID (the Airtable primary text). */
  leadId: z.string(),
  /** Client name. */
  name: z.string(),
  /** Lifecycle status (one of the 11 `Status` enum values). */
  status: z.string().nullable(),
  /** Case-type label as recorded by the parser. */
  caseType: z.string().nullable(),
  /** Narrative summary from the parser. */
  caseSummary: z.string().nullable(),

  /** User-set `Creation Date` (YYYY-MM-DD) — the matter's real historical
   *  date, distinct from the system `createdTime`. For migrated records the
   *  system createdTime is the import moment (engineering/contract/provenance.ts
   *  migration_2026_04), but this field survived with the true date; the
   *  Placed book anchors migrated-case age on it. Null
   *  when Airtable omits it (shouldn't happen — Airtable auto-fills it). */
  creationDate: z.string().nullable(),

  /** Originating Firm — linked record ID, or null when the parser did
   *  not match a firm in the catalog. */
  originatingFirmId: z.string().nullable(),

  /** Date of the underlying incident (YYYY-MM-DD), when the parser
   *  could extract one. */
  incidentDate: z.string().nullable(),
  /** Two-letter US state of the incident — used as "jurisdiction" in
   *  the verification UI. */
  incidentState: z.string().nullable(),

  phone: z.string().nullable(),
  /** Secondary contact number. Optional everywhere — only populated when
   *  the parser found a second number or the operator added one. Member of
   *  the LEAD_EDITABLE allowlist (slice 5c). */
  phone2: z.string().nullable(),
  email: z.string().nullable(),

  /** Client's state of residence (distinct from `incidentState`). Both
   *  matter for venue + eligibility on multi-jurisdictional matters. Member
   *  of LEAD_EDITABLE. */
  residentState: z.string().nullable(),

  /** Mailing address. Free-form multilineText. Member of LEAD_EDITABLE. */
  address: z.string().nullable(),

  /** TRUE when the inbound matter was already a signed Transfer
   *  (vs. a Lead awaiting client signature). */
  signed: z.boolean(),

  /** Side-channel Withdrawn flag — the client opted out. Coexists with
   *  the current status rather than displacing it; the system stops
   *  routing and nudging once set. The Under Review book excludes withdrawn
   *  leads (a withdrawn matter isn't a clog to shepherd). Airtable omits
   *  unchecked checkboxes, so missing → false. */
  withdrawn: z.boolean(),

  /** Date (YYYY-MM-DD) the client withdrew (Leads `Withdrawn Date`,
   *  fld2dvgBwQFH5cV6f) — distinct from the `withdrawn` checkbox above.
   *  Wired to nothing in CC before the metrics substrate; the atom extractor
   *  is its first reader. Null when unset. `.default(null)` so a
   *  payload predating this field still parses (absence = null); the projector
   *  always writes it, and the inferred type stays `string | null`. */
  withdrawnDate: z.string().nullable().default(null),

  /** Date (YYYY-MM-DD) the lead reached Status="Rejected" (#605 spec / EOS
   *  canon rebuild pt 12). Stamped by the portal's three canonical Rejected
   *  writers in the same PATCH as the Status flip; cleared by the paths that
   *  move a lead OUT of Rejected (revive, reverse_decision reopen). Null on
   *  rejections before the day every writer stamped it
   *  (engineering/contract/provenance.ts rejected_date_every_writer). The EOS
   *  resolved-only conversion denominator reads this. */
  rejectedDate: z.string().nullable(),

  /** Date (YYYY-MM-DD) of the matter's statute of limitations (Leads
   *  `Statute of Limitations`, fldyXr1kGvKJOGZRE) — the hard legal cliff on the
   *  CLAIM. Distinct from the Placement's `nextFilingDeadline`, which is the
   *  handling firm's rolling deadline and does not exist until a firm holds the
   *  matter. Written by the Lead Parser when it can extract the date, by an
   *  operator via the ops MCP (#662), and by the handling firm (#828). Block 0 reads it.
   *  `.default(null)` so a payload predating this field still parses (absence =
   *  null), matching the `withdrawnDate` precedent. */
  statuteOfLimitations: z.string().nullable().default(null),

  /** Attachments uploaded by the parser to the `Intake Docs` field.
   *  Always an array (empty when none). */
  intakeDocs: z.array(LeadAttachmentSchema),

  /** Parser self-reports — verbatim text from `Internal Notes` when
   *  the parser flagged something (e.g., a hallucinated firm it
   *  cleared). Null when the parser had nothing to flag. */
  internalNotes: z.string().nullable(),

  /** Raw "From:" address the parser saw. Used for email-group
   *  correlation. Null when the parser did not capture it. */
  senderEmail: z.string().nullable(),
  /** Domain portion of senderEmail. */
  senderDomain: z.string().nullable(),

  /** Linked Placement record IDs (Airtable `multipleRecordLinks` → Placements).
   *  Always an array; empty when the lead has no placements yet. Slice 5c uses
   *  this on the reject cascade — every non-terminal placement on the lead is
   *  auto-declined before the lead status flips to Rejected. */
  placements: z.array(z.string()),
});
export type Lead = z.infer<typeof LeadSchema>;
